import { useRouter } from 'next/router'
import { Product } from '@mc/api'
import { Icon } from '@mc/design-system'
import {
  StyledFeatureVariable,
  PROMO_VARIABLE_NAMES,
} from '@mc/feature-variables'
import { usePrefersReducedMotion } from '@mc/hooks'
import { Trans, useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { usePromotion, PROMO_FEATURE_FLAG } from '@mc/promo-utils'
import { useStaticData } from '@mc/static-data-react'
import CTA from 'components/CTA'
import { GIFT_PAGE_REDESIGN_I18N_NAMESPACE } from '../i18n'
import { Video } from './Video'

export const Hero = () => {
  const prefersReducedMotion = usePrefersReducedMotion()

  return (
    <div className='container'>
      <div className='row align-items-start mc-my-md-12'>
        <div className='col-12 col-md-6'>
          <Video prefersReducedMotion={prefersReducedMotion} />
        </div>
        <div className='col-12 col-md-6'>
          <div className='row no-gutters'>
            <div className='col-12 col-xl-9'>
              <div className='mc-mt-md-10 mc-text--center mc-text-md--left'>
                <Title />

                <div className='d-none d-md-block'>
                  <GiftButton />
                </div>
                <div className='d-block d-md-none'>
                  <GiftButton fullWidth />
                </div>

                <GiftValueProps />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const Title = () => {
  const { t } = useTranslation(GIFT_PAGE_REDESIGN_I18N_NAMESPACE)

  return (
    <>
      <h1 className='mc-text-h1 mc-text--bold mc-mb-3'>
        <StyledFeatureVariable
          flagName={PROMO_FEATURE_FLAG}
          variableName={PROMO_VARIABLE_NAMES.giftPageTitle}
          defaultCopy={
            <Trans ns={GIFT_PAGE_REDESIGN_I18N_NAMESPACE} i18nKey='hero.title'>
              <br />
            </Trans>
          }
        />
      </h1>
      <p className='mc-text-large mc-text-color--medium'>
        <StyledFeatureVariable
          flagName={PROMO_FEATURE_FLAG}
          variableName={PROMO_VARIABLE_NAMES.giftPageSubtitle}
          defaultCopy={t('hero.subtitle')}
        />
      </p>
    </>
  )
}

const color = 'rgba(255, 255, 255, 0.70)'

const GiftButton = ({ fullWidth = false }: { fullWidth?: boolean }) => {
  const { annualPass } = useStaticData<{ annualPass: Product }>()
  const { isPromotionOn } = usePromotion({ isGift: true })
  const { t } = useTranslation(GIFT_PAGE_REDESIGN_I18N_NAMESPACE)
  const { query } = useRouter()

  const showDiscount =
    isPromotionOn &&
    !!annualPass.monetizedFlatRateTotal &&
    !!annualPass.coupon?.percentage

  if (showDiscount) {
    return (
      <div className='mc-my-8'>
        <p className='d-flex justify-content-center justify-content-md-start align-items-center mc-mb-2 mc-text-small mc-text-color--success'>
          <Icon
            className='mc-icon--3 mc-opacity--hinted mc-mr-1'
            kind='check'
          />
          <span>
            {t('hero.promo.discount', {
              percentage: annualPass.coupon?.percentage,
            })}
          </span>
        </p>

        <CTA
          className='mc-p-5'
          trackProps={{
            action: 'aap-gift-landing',
            content_type: 'product',
            location: 'gifting-landing-hero',
          }}
          fullWidth={fullWidth}
          href={paths.checkoutGift(query)}
        >
          <Trans
            ns={GIFT_PAGE_REDESIGN_I18N_NAMESPACE}
            i18nKey='hero.promo.cta'
            values={{
              strikeThroughPrice: annualPass.monetizedFlatRate,
              price: annualPass.monetizedFlatRateTotal,
            }}
          >
            <span className='mc-mr-1' />
            <span
              className='mc-text--strike mc-text--light mc-mr-1'
              style={{ color }}
            />
            <span className='mc-mr-1' />
            <span
              className='mc-text--light mc-text--lowercase'
              style={{ color }}
            />
          </Trans>
        </CTA>
      </div>
    )
  }

  return (
    <div className='mc-my-8'>
      <CTA
        className='mc-p-5'
        trackProps={{
          action: 'aap-gift-landing',
          content_type: 'product',
          location: 'gifting-landing-hero',
        }}
        fullWidth={fullWidth}
        href={paths.checkoutGift(query)}
      >
        <Trans
          ns={GIFT_PAGE_REDESIGN_I18N_NAMESPACE}
          i18nKey='hero.cta'
          values={{ price: annualPass.monetizedFlatRate }}
        >
          <span className='mc-mr-1' />
          <span
            className='mc-text--light mc-text--lowercase'
            style={{
              color: 'rgba(255, 255, 255, 0.70)',
            }}
          />
        </Trans>
      </CTA>
    </div>
  )
}

const GiftValueProps = () => {
  const { t } = useTranslation(GIFT_PAGE_REDESIGN_I18N_NAMESPACE)

  return (
    <ul>
      <Prop text={t('hero.valueProps.includes', { numberOfClasses: 200 })} />
      <Prop text={t('hero.valueProps.schedule')} />
      <Prop text={t('hero.valueProps.printable')} />
      <Prop text={t('hero.valueProps.moneyBack')} />
    </ul>
  )
}

const Prop = ({ text }: { text: string }) => (
  <li className='d-flex align-items-center justify-content-center justify-content-md-start mc-mb-1'>
    <Icon
      className='mc-icon--3 mc-opacity--hinted mc-mr-1 mc-text-color--success'
      kind='check'
    />
    <p className='mc-text-color--light'>{text}</p>
  </li>
)
